import React, { useState } from 'react';
import { Helmet } from 'react-helmet-async';
import { 
  User, 
  Mail, 
  Phone, 
  Calendar, 
  Edit, 
  Save, 
  X,
  Award,
  Clock,
  TrendingUp
} from 'lucide-react';
import { useAuth } from '@/contexts/AuthContext';
import { motion } from 'framer-motion';
import { UserProfileForm } from '@/types';
import { ProfilePhotoUpload } from '@/components/ui/ProfilePhotoUpload';
import { toast } from 'react-hot-toast';

const Profile: React.FC = () => {
  const { currentUser, updateUserProfile, refreshUserData } = useAuth();
  const [isEditing, setIsEditing] = useState(false);
  const [loading, setLoading] = useState(false);
  
  // Log para depuración
  console.log('🔍 Profile - currentUser:', currentUser);
  console.log('🔍 Profile - profilePhotoUrl:', currentUser?.profilePhotoUrl);
  
  const [formData, setFormData] = useState({
    firstName: currentUser?.firstName || '',
    lastName: currentUser?.lastName || '',
    phone: currentUser?.phone || '',
  });

  // Forzar recarga de datos al montar el componente
  React.useEffect(() => {
    console.log('🔄 Profile - Forzando recarga de datos del usuario...');
    refreshUserData();
  }, []);

  const handleSave = async () => {
    setLoading(true);
    try {
      await updateUserProfile(formData);
      setIsEditing(false);
      toast.success('Perfil actualizado correctamente');
    } catch (error) {
      console.error('Error updating profile:', error);
      toast.error('Error al actualizar el perfil');
    } finally {
      setLoading(false);
    }
  };

  const handleCancel = () => {
    setFormData({
      firstName: currentUser?.firstName || '',
      lastName: currentUser?.lastName || '',
      phone: currentUser?.phone || '',
    });
    setIsEditing(false);
  };

  const handleChange = (e: React.ChangeEvent<HTMLInputElement | HTMLTextAreaElement>) => {
    const { name, value } = e.target;
    setFormData(prev => ({
      ...prev,
      [name]: value,
    }));
  };



  const getMembershipStatusColor = () => {
    if (currentUser?.membershipType === 'monthly_fee') {
      return currentUser?.paymentStatus === 'paid' ? 'success' : 'warning';
    }
    return currentUser?.activePass?.creditsRemaining ? 'success' : 'error';
  };

  const getMembershipStatusText = () => {
    if (currentUser?.membershipType === 'monthly_fee') {
      return currentUser?.paymentStatus === 'paid' ? 'Activa' : 'Pendiente';
    }
    return currentUser?.activePass?.creditsRemaining ? 'Activa' : 'Expirada';
  };

  return (
    <>
      <Helmet>
        <title>Mi Perfil</title>
      </Helmet>

      <div className="space-y-6">
        {/* Header */}
        <motion.div
          initial={{ opacity: 0, y: 20 }}
          animate={{ opacity: 1, y: 0 }}
          transition={{ duration: 0.5 }}
          className="flex flex-col sm:flex-row sm:items-center sm:justify-between"
        >
          <div>
            <h1 className="text-3xl font-bold text-amber-400">
              Mi Perfil
            </h1>
            <p className="text-gray-400">
              Gestiona tu información personal
            </p>
          </div>
          <div className="mt-4 sm:mt-0">
            {!isEditing ? (
              <button
                onClick={() => setIsEditing(true)}
                className="px-4 py-2 bg-primary-500 hover:bg-primary-600 text-white rounded-lg font-medium flex items-center transition-colors"
              >
                <Edit className="mr-2 h-4 w-4" />
                Editar Perfil
              </button>
            ) : (
              <div className="flex space-x-2">
                <button
                  onClick={handleCancel}
                  className="px-4 py-2 bg-gray-700 hover:bg-gray-600 text-white rounded-lg font-medium flex items-center transition-colors border border-gray-600"
                >
                  <X className="mr-2 h-4 w-4" />
                  Cancelar
                </button>
                <button
                  onClick={handleSave}
                  disabled={loading}
                  className="px-4 py-2 bg-green-600 hover:bg-green-700 text-white rounded-lg font-medium flex items-center transition-colors disabled:opacity-50"
                >
                  <Save className="mr-2 h-4 w-4" />
                  {loading ? 'Guardando...' : 'Guardar'}
                </button>
              </div>
            )}
          </div>
        </motion.div>

        <div className="grid grid-cols-1 lg:grid-cols-3 gap-6">
          {/* Profile Information */}
          <motion.div
            initial={{ opacity: 0, y: 20 }}
            animate={{ opacity: 1, y: 0 }}
            transition={{ duration: 0.5, delay: 0.1 }}
            className="lg:col-span-2 space-y-6"
          >
            {/* Personal Information */}
            <div className="bg-gray-800 rounded-xl border border-gray-700 overflow-hidden">
              <div className="px-6 py-4 border-b border-gray-700 bg-gray-800/50">
                <h3 className="text-lg font-semibold text-white flex items-center">
                  <User className="mr-2 h-5 w-5 text-primary-400" />
                  Información Personal
                </h3>
              </div>
              <div className="p-6 space-y-6">
                {/* Foto de Perfil */}
                <div className="border-2 border-dashed border-gray-600 rounded-xl p-6 bg-gray-900/50">
                  <div className="text-center">
                    <h4 className="text-base font-medium text-gray-300 mb-4">
                      📸 Foto de Perfil
                    </h4>
                    
                    <ProfilePhotoUpload
                      currentPhotoUrl={currentUser?.profilePhotoUrl}
                      onPhotoUpload={async (photoUrl) => {
                        try {
                          await updateUserProfile({ profilePhotoUrl: photoUrl });
                          toast.success('Foto de perfil actualizada correctamente');
                        } catch (error) {
                          console.error('Error actualizando foto:', error);
                          toast.error('Error al actualizar la foto');
                        }
                      }}
                      onPhotoRemove={async () => {
                        try {
                          await updateUserProfile({ profilePhotoUrl: undefined });
                          toast.success('Foto de perfil eliminada');
                        } catch (error) {
                          console.error('Error eliminando foto:', error);
                          toast.error('Error al eliminar la foto');
                        }
                      }}
                      size="lg"
                      className="mx-auto"
                    />
                  </div>
                </div>

                <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                  <div>
                    <label className="block text-sm font-medium text-gray-300 mb-2">Nombre</label>
                    {isEditing ? (
                      <input
                        type="text"
                        name="firstName"
                        value={formData.firstName}
                        onChange={handleChange}
                        className="w-full px-3 py-2 bg-gray-800 border border-gray-600 rounded-lg text-white placeholder-gray-400 focus:ring-2 focus:ring-primary-500 focus:border-transparent"
                      />
                    ) : (
                      <p className="text-white text-lg">{currentUser?.firstName}</p>
                    )}
                  </div>
                  <div>
                    <label className="block text-sm font-medium text-gray-300 mb-2">Apellido</label>
                    {isEditing ? (
                      <input
                        type="text"
                        name="lastName"
                        value={formData.lastName}
                        onChange={handleChange}
                        className="w-full px-3 py-2 bg-gray-800 border border-gray-600 rounded-lg text-white placeholder-gray-400 focus:ring-2 focus:ring-primary-500 focus:border-transparent"
                      />
                    ) : (
                      <p className="text-white text-lg">{currentUser?.lastName}</p>
                    )}
                  </div>
                </div>

                <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                  <div>
                    <label className="block text-sm font-medium text-gray-300 mb-2">Email</label>
                    <p className="text-white flex items-center">
                      <Mail className="mr-2 h-4 w-4 text-primary-400" />
                      {currentUser?.email}
                    </p>
                  </div>
                  <div>
                    <label className="block text-sm font-medium text-gray-300 mb-2">Teléfono</label>
                    {isEditing ? (
                      <input
                        type="tel"
                        name="phone"
                        value={formData.phone}
                        onChange={handleChange}
                        className="w-full px-3 py-2 bg-gray-800 border border-gray-600 rounded-lg text-white placeholder-gray-400 focus:ring-2 focus:ring-primary-500 focus:border-transparent"
                        placeholder="+34 600 000 000"
                      />
                    ) : (
                      <p className="text-white flex items-center">
                        <Phone className="mr-2 h-4 w-4 text-primary-400" />
                        {currentUser?.phone || 'No especificado'}
                      </p>
                    )}
                  </div>
                </div>
              </div>
            </div>
          </motion.div>

          {/* Sidebar */}
          <motion.div
            initial={{ opacity: 0, y: 20 }}
            animate={{ opacity: 1, y: 0 }}
            transition={{ duration: 0.5, delay: 0.2 }}
            className="space-y-6"
          >
            {/* Membership Status */}
            <div className="bg-gray-800 rounded-xl border border-gray-700 overflow-hidden">
              <div className="px-6 py-4 border-b border-gray-700 bg-gray-800/50">
                <h3 className="text-lg font-semibold text-white flex items-center">
                  <Award className="mr-2 h-5 w-5 text-amber-400" />
                  Membresía
                </h3>
              </div>
              <div className="p-6 space-y-4">
                <div>
                  <p className="text-sm text-gray-400 mb-1">Tipo</p>
                  <p className="font-semibold text-white capitalize">
                    {currentUser?.membershipType?.replace('_', ' ') || 'N/A'}
                  </p>
                </div>
                <div>
                  <p className="text-sm text-gray-400 mb-1">Estado</p>
                  <span className={`inline-flex items-center px-3 py-1 rounded-full text-xs font-medium ${
                    getMembershipStatusColor() === 'success'
                      ? 'bg-green-500/20 text-green-400 border border-green-500/30'
                      : getMembershipStatusColor() === 'warning'
                      ? 'bg-yellow-500/20 text-yellow-400 border border-yellow-500/30'
                      : 'bg-red-500/20 text-red-400 border border-red-500/30'
                  }`}>
                    {getMembershipStatusText()}
                  </span>
                </div>
                {currentUser?.membershipType === 'class_pass' && currentUser?.activePass && (
                  <div>
                    <p className="text-sm text-gray-400 mb-1">Clases Restantes</p>
                    <p className="font-semibold text-white">
                      {currentUser.activePass.creditsRemaining} / {currentUser.activePass.creditsRemaining + (10 - currentUser.activePass.creditsRemaining)}
                    </p>
                    <p className="text-xs text-gray-500 mt-1">
                      Expira: {new Date(currentUser.activePass.expirationDate).toLocaleDateString('es-ES')}
                    </p>
                  </div>
                )}
              </div>
            </div>

            {/* Account Stats */}
            <div className="bg-gray-800 rounded-xl border border-gray-700 overflow-hidden">
              <div className="px-6 py-4 border-b border-gray-700 bg-gray-800/50">
                <h3 className="text-lg font-semibold text-white flex items-center">
                  <TrendingUp className="mr-2 h-5 w-5 text-primary-400" />
                  Estadísticas
                </h3>
              </div>
              <div className="p-6 space-y-4">
                <div className="flex items-center justify-between p-3 bg-gray-900/50 rounded-lg">
                  <div className="flex items-center gap-3">
                    <div className="w-10 h-10 rounded-lg bg-primary-500/20 flex items-center justify-center">
                      <Calendar className="h-5 w-5 text-primary-400" />
                    </div>
                    <div>
                      <p className="text-sm text-gray-400">Miembro desde</p>
                      <p className="font-semibold text-white">
                        {currentUser?.createdAt 
                          ? new Date(currentUser.createdAt).toLocaleDateString('es-ES', {
                              year: 'numeric',
                              month: 'long',
                              day: 'numeric'
                            })
                          : 'N/A'
                        }
                      </p>
                    </div>
                  </div>
                </div>
              </div>
            </div>
          </motion.div>
        </div>
      </div>
    </>
  );
};

export default Profile; 